import { Button, ButtonThemesEnum } from '@propertyfinder/button';
import { DropdownSelect } from '@propertyfinder/dropdown';
import { TextField } from '@propertyfinder/text-field';
import { ToggleComponent } from '@propertyfinder/toggle';
import { Fragment, FunctionComponent, h } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';

import { SaveSearchFrequencyEnum } from 'common/data/save-search/frequency.enum';
import { configGetLanguage } from 'common/helper/config/get-language';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { CompleteProfileAsyncComponent } from 'common/module/complete-profile/async.component';
import { InputValidatorMaxLength } from 'common/module/input/validator/max-length';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { SaveSearchFormPropsInterface } from 'common/module/save-search/form/props.interface';
import { useSaveSearchFormField } from 'common/module/save-search/form/use-field';
import { SaveSearchFormViewStore } from 'common/module/save-search/form/view-store';
import { saveSearchFrequencies } from 'common/module/save-search/frequencies';

export const SaveSearchFormSectionComponent: FunctionComponent<SaveSearchFormPropsInterface> = ({
  title,
  fields,
  userHasEmail,
  error,
  isLoading,
  onSubmit,
  sendTealiumEvent,
}) => {
  const isRtl = configGetLanguage().isRtl;
  const [generalError, setGeneralError] = useState(error);
  const [displayCompleteProfile, setDisplayCompleteProfile] = useState(null);
  const [notifyMe, setNotifyMe] = useState(userHasEmail);
  const saveSearchFrequenciesList = saveSearchFrequencies.filter((data) => data.value !== SaveSearchFrequencyEnum.OFF);
  const [frequency, setFrequency] = useState(saveSearchFrequenciesList[1]);
  const [defaultFrequencyVal] = useSaveSearchFormField(fields.frequency);
  const [name, setName] = useSaveSearchFormField(fields.name, [
    new InputValidatorRequired(i18nTranslate('save_search:name:validation:required')),
    new InputValidatorMaxLength(
      i18nTranslate('save_search:name:validation:character_limit'),
      SaveSearchFormViewStore.MAX_CHARACTERS_LIMIT,
      true
    ),
  ]);

  useEffect(() => {
    let selectedOption = defaultFrequencyVal.value;
    if (!userHasEmail || defaultFrequencyVal.value === SaveSearchFrequencyEnum.OFF) {
      setNotifyMe(false);
      selectedOption = SaveSearchFrequencyEnum.DAILY;
    } else {
      setNotifyMe(true);
    }

    if (!defaultFrequencyVal.value) {
      selectedOption = SaveSearchFrequencyEnum.DAILY;
    }

    setFrequency(saveSearchFrequenciesList.find((item) => item.value === selectedOption));
  }, []);

  const onNotifyMeClick = useCallback(() => {
    sendTealiumEvent('open');
    userHasEmail ? setNotifyMe(!notifyMe) : setDisplayCompleteProfile(true);
  }, [userHasEmail, notifyMe]);

  useEffect(() => {
    setGeneralError(error);
  }, [error]);

  return (
    <Fragment>
      <div className='save-search-section__left-section'>
        <img
          src={__('assets/common/save-search.svg')}
          className={'save-search-section__vp-image'}
          alt={'save search'}
        />
      </div>
      <div className='save-search-section__right-section'>
        <div>
          <div className={'save-search-section__input'}>
            {!fields.formatted_filters ? (
              <p className='save-search-section__info'>{i18nTranslate('save_search:frequency:help_text')}</p>
            ) : (
              <p className='save-search-section__info'>{fields.formatted_filters}</p>
            )}
            <TextField
              htmlAttributes={{
                type: 'text',
              }}
              placeholder={i18nTranslate('save_search:name:label')}
              value={name.value}
              errorText={name.error}
              disabled={isLoading}
              onInput={(e: InputEvent): void => {
                setGeneralError('');
                setName((e.target as HTMLInputElement).value);
              }}
            />
          </div>

          <div>
            <div className={'save-search-section__toggle-notify-me'}>
              <span className={'save-search-section__span-notify-me'}>{i18nTranslate('SaveSearch_NotifyMeLabel')}</span>
              <ToggleComponent checked={notifyMe} onClick={onNotifyMeClick} />
            </div>
            <p className={'save-search-section__p-change-setting'}>
              {i18nTranslate('SaveSearch_NotifyMeLabelHelpText')}
            </p>
          </div>

          {notifyMe && (
            <DropdownSelect
              triggerClassName={'save-search-section__dropdown-frequency'}
              popoverClassName={'save-search-section dropdown-popover-frequency'}
              options={saveSearchFrequenciesList}
              rtl={isRtl}
              onChange={(selectedItem: typeof frequency): void => {
                setGeneralError('');
                setFrequency(selectedItem);
              }}
              mapToLabel={(selectedItem: typeof frequency): string => selectedItem.label}
              mapToValue={(selectedItem: typeof frequency): string => selectedItem.value}
              value={frequency}
            />
          )}
          {generalError && <div className='save-search-section__error--general'>{generalError}</div>}
        </div>
        <Button
          theme={ButtonThemesEnum.PRIMARY}
          onClick={(): void => {
            const searchName = name.value?.trim();
            if (!searchName || name.error) {
              setName(searchName);
              return;
            }
            setGeneralError('');
            onSubmit(searchName, notifyMe ? frequency.value : SaveSearchFrequencyEnum.OFF, fields.id);
          }}
          className='save-search-section__button'
          disabled={isLoading || !name.value?.trim()}
          loading={isLoading}
        >
          {i18nTranslate(title)}
        </Button>
      </div>

      {displayCompleteProfile && (
        <CompleteProfileAsyncComponent
          showOverlay={false}
          onSuccess={(): void => {
            setNotifyMe(true);
            setDisplayCompleteProfile(false);
            sendTealiumEvent('submit');
          }}
          onCancel={(): void => {
            setDisplayCompleteProfile(false);
            sendTealiumEvent('skip');
          }}
          onClose={(): void => setDisplayCompleteProfile(false)}
        />
      )}
    </Fragment>
  );
};
